import { Alert, Anchor, Button, FileButton, Group, Loader, Modal, Stack, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { rawApi, rawApiFile } from '../api/client';
import { ApiError } from '../api/errors';
import { detailKey } from '../api/queryKeys';
import { formatDate } from '../lib/format';
import { notifyDone, notifyError } from '../lib/notify';

/**
 * The photograph of the piece of paper.
 *
 * A gas bill is on the counter. Somebody photographs it on a phone and types the figure in, and
 * the paper goes wherever paper goes. Six weeks later there is a question about whether the meter
 * reading was right — and by then the photograph is the only surviving record of what the man
 * actually wrote down.
 *
 * The column to hold one has existed since the first migration and the assistant could set it.
 * Nothing on any screen could put a photograph there or show one, so the column was write-only and
 * a bill recorded by hand could never carry its own evidence. This is the control that closes it,
 * written once because it belongs in four places at least: on the form where a bill is written
 * down, on the form where a delivery is received, on the bill afterwards, and on the delivery
 * afterwards — the last two being where it actually gets used, because the photograph almost never
 * arrives at the same moment as the figure.
 *
 * ## The three states, and why they are three and not two
 *
 * **Nothing attached.** Nobody took a picture. This is not an error, it is most bills, and it says
 * so quietly and offers the way to fix it.
 *
 * **Attached and there.** A thumbnail-sized way in, opening the picture full size.
 *
 * **Attached and GONE.** The upload was recorded and the file is not in storage any more. That is
 * a different sentence from "nobody took a picture" and it must not be shown as the same one: the
 * first tells somebody to go and look for the paper, the second tells them there is nothing to
 * look for. Neither is an error page.
 */

const DOCUMENTS_PATH = '/admin/v1/suppliers/documents';

/** The bound the API enforces. Repeated here so the refusal arrives before the upload does. */
const MAX_BYTES = 10 * 1024 * 1024;

/**
 * What the file picker offers. A hint to the operating system's dialog and nothing more — the
 * platform decides what a file IS from its bytes, and a picker that let something else through
 * would still be refused with a sentence that says why.
 */
const ACCEPT = 'image/jpeg,image/png,image/heic,image/heif,image/webp,application/pdf';

export interface StoredPaper {
  id: string;
  contentType: string;
  sizeBytes: number;
  kind: string;
  uploadedAt: string;
  /** False when the record of the upload survives but the file does not. */
  available: boolean;
}

function readableSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** What it is, in a word, so a row can say "PDF" rather than "application/pdf". */
function paperKindWord(contentType: string): string {
  return contentType === 'application/pdf' ? 'PDF' : 'Photo';
}

/**
 * Reading a file the way the API takes it.
 *
 * Chunked deliberately: `String.fromCharCode(...bytes)` on a four-megabyte photograph spreads four
 * million arguments onto the call stack and throws, which on a phone photograph is every time.
 */
async function toBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

// ------------------------------------------------------------------ looking at one

/**
 * The picture, full size, in front of the figure it is evidence for.
 *
 * Fetched through the same bearer as every other request — there is no link to this file that
 * works without one, which is the point: these are photographs of the bar's own financial
 * paperwork. The bytes become an object URL that lives exactly as long as the dialog does.
 */
export function PaperViewer({
  documentId,
  title,
  onClose,
}: {
  documentId: string;
  title: string;
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [type, setType] = useState<string>('');
  const [failure, setFailure] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    void (async () => {
      try {
        const blob = await rawApiFile(`${DOCUMENTS_PATH}/${documentId}/file`);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setType(blob.type);
        setUrl(objectUrl);
      } catch (err) {
        if (cancelled) return;
        // The one failure worth its own sentence. Everything else falls through to the shared copy.
        setFailure(
          err instanceof ApiError && err.code === 'document_gone'
            ? {
                title: 'This photograph is no longer stored',
                message:
                  'It was attached to this record, and the file itself has gone. If you still have the paper, photograph it again and attach the new one.',
              }
            : {
                title: 'It could not be opened',
                message: 'Nothing has been lost. Try again in a moment.',
              },
        );
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentId]);

  return (
    <Modal opened onClose={onClose} title={title} size="xl">
      <Stack gap="md">
        {failure ? (
          <Alert color="yellow" variant="light" title={failure.title}>
            {failure.message}
          </Alert>
        ) : !url ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : type === 'application/pdf' ? (
          // An <object> rather than an <img>: a supplier's accounts system sends PDFs, and a
          // browser that cannot show one inline falls through to the link underneath.
          <object data={url} type="application/pdf" width="100%" height="640" aria-label={title}>
            <Text size="sm">This browser will not show the file here. Open it in a new tab instead.</Text>
          </object>
        ) : (
          // Plain <img> on purpose. An iPhone photographs in HEIC and some browsers will not draw
          // one; when that happens the picture is replaced by the sentence below rather than by a
          // broken-image icon that explains nothing.
          <PaperImage url={url} alt={title} />
        )}

        <Group justify="space-between">
          {url ? (
            <Anchor href={url} target="_blank" rel="noopener" size="sm">
              Open it in a new tab
            </Anchor>
          ) : (
            <span />
          )}
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function PaperImage({ url, alt }: { url: string; alt: string }) {
  const [broken, setBroken] = useState(false);
  if (broken) {
    return (
      <Alert color="gray" variant="light" title="This browser cannot show this picture">
        The file is on file and undamaged — some browsers will not draw a photograph taken by an iPhone. Open
        it in a new tab and it will show.
      </Alert>
    );
  }
  return (
    // A blob URL from an authenticated fetch, not an asset: nothing here is a static image.
    <img
      src={url}
      alt={alt}
      onError={() => setBroken(true)}
      style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto' }}
    />
  );
}

// ------------------------------------------------------------------ attaching one, from anywhere

/**
 * Uploading a file and putting it on a record, in one act.
 *
 * Two steps at the API — store the file, then point the record at it — and deliberately one step
 * here. Nobody attaching a photograph of a propane bill is thinking about a document library, and
 * a screen that made them upload first and attach second would be a screen with a step in it that
 * exists because of how the storage is arranged.
 */
function usePaperAttachment(
  attachPath: string,
  subject: string,
  kind: string,
  invalidate: readonly (readonly unknown[])[] | undefined,
) {
  const queryClient = useQueryClient();

  const settle = (paperId: string | null) => {
    for (const key of invalidate ?? []) void queryClient.invalidateQueries({ queryKey: key });
    if (paperId) void queryClient.invalidateQueries({ queryKey: detailKey(DOCUMENTS_PATH, paperId) });
  };

  const attach = useMutation({
    mutationFn: async (file: File) => {
      // Checked here as well as at the API, so a photograph too big to send is refused before it
      // is read into memory and encoded rather than after.
      if (file.size > MAX_BYTES) throw new ApiError(400, 'too_large', 'too large');
      const stored = await rawApi<StoredPaper>('POST', DOCUMENTS_PATH, {
        base64Content: await toBase64(file),
        kind,
      });
      await rawApi('POST', attachPath, { sourceDocumentId: stored.id });
      return stored;
    },
    onSuccess: (stored) => {
      settle(stored.id);
      notifyDone('Photograph attached', `It is on ${subject} from now on.`);
    },
    onError: (err: Error) => notifyError(err, 'attach that photograph'),
  });

  const remove = useMutation({
    mutationFn: () => rawApi('POST', attachPath, { sourceDocumentId: null }),
    onSuccess: () => {
      settle(null);
      notifyDone('Taken off', `${subject} no longer has a photograph on it. The file itself is kept.`);
    },
    onError: (err: Error) => notifyError(err, 'take that photograph off'),
  });

  return { attach, remove, busy: attach.isPending || remove.isPending };
}

// ------------------------------------------------------------------ the way in from a list

/**
 * What a row on a list says about the paperwork behind its figure.
 *
 * On the payables list, on the disagreements list, on a vendor's own bills and on the deliveries —
 * because those are the screens where somebody is deciding whether a number is right, and a
 * document nobody can find from where they are standing is the same as no document.
 *
 * It also ATTACHES, from the row, which is not a convenience. The moment a person has the paper in
 * their hand is the moment they are looking at the bill on this list; sending them into a detail
 * screen to attach it is how a photograph does not get taken.
 *
 * Deliberately does NOT ask whether the file is still in storage. That is one request per row on a
 * list forty long, to decorate a link that says the same thing either way — the truth arrives when
 * it is opened, which is the moment somebody actually wants it.
 */
export function PaperRowControl({
  documentId,
  attachPath,
  subject,
  invalidate,
  kind = 'invoice',
}: {
  documentId: string | null | undefined;
  attachPath: string;
  subject: string;
  invalidate?: readonly (readonly unknown[])[];
  kind?: 'invoice' | 'photo' | 'statement' | 'other';
}) {
  const [open, setOpen] = useState(false);
  const { attach, busy } = usePaperAttachment(attachPath, subject, kind, invalidate);

  if (documentId) {
    return (
      <>
        <Anchor component="button" type="button" size="xs" onClick={() => setOpen(true)}>
          See the paper
        </Anchor>
        {open && <PaperViewer documentId={documentId} title={subject} onClose={() => setOpen(false)} />}
      </>
    );
  }
  return (
    <FileButton onChange={(file) => file && attach.mutate(file)} accept={ACCEPT}>
      {(props) => (
        <Anchor {...props} component="button" type="button" size="xs" c="dimmed" disabled={busy}>
          {busy ? 'Attaching…' : 'Attach the paper'}
        </Anchor>
      )}
    </FileButton>
  );
}

// ------------------------------------------------------------------ on a form, before the record exists

/**
 * The picker inside a form that is still being filled in.
 *
 * It uploads AS SOON AS a file is chosen rather than on submit, and hands back the id for the form
 * to send with everything else. That ordering is deliberate: the refusal for a file that is not a
 * photograph or a PDF arrives while the person is still looking at the file picker, instead of
 * throwing away a completed bill because of the last box on it.
 *
 * A document uploaded and then abandoned is a row and some bytes nobody points at. That is the
 * right trade — it costs a fraction of a cent and it is the only ordering in which the person is
 * told about a bad file at the moment they chose it.
 */
export function PaperPicker({
  value,
  onChange,
  label = 'Photograph of the bill',
  description = 'Optional. A photo or a PDF — this is what answers the question when the paper has gone.',
  kind = 'invoice',
  disabled,
}: {
  value: StoredPaper | null;
  onChange: (paper: StoredPaper | null) => void;
  label?: string;
  description?: string;
  kind?: 'invoice' | 'photo' | 'statement' | 'other';
  disabled?: boolean;
}) {
  const [viewing, setViewing] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      // Checked here as well as at the API, so a photograph too big to send is refused before it
      // is read into memory and encoded rather than after.
      if (file.size > MAX_BYTES) {
        throw new ApiError(400, 'too_large', 'too large');
      }
      return rawApi<StoredPaper>('POST', DOCUMENTS_PATH, {
        base64Content: await toBase64(file),
        kind,
      });
    },
    onSuccess: (paper) => {
      setRefusal(null);
      onChange(paper);
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.code === 'too_large') {
        return setRefusal('That file is bigger than 10 MB. Choose a smaller photo or PDF.');
      }
      if (err instanceof ApiError && err.code === 'unsupported_type') {
        return setRefusal('That is not a photo or a PDF. Pick the picture you took of the paper.');
      }
      notifyError(err, 'attach that file');
    },
  });

  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        {label}
      </Text>
      <Text size="xs" c="dimmed">
        {description}
      </Text>
      <Group gap="sm">
        <FileButton onChange={(file) => file && upload.mutate(file)} accept={ACCEPT}>
          {(props) => (
            <Button
              {...props}
              size="compact-sm"
              variant="default"
              loading={upload.isPending}
              disabled={disabled}
            >
              {value ? 'Choose a different one' : 'Attach a photo or PDF'}
            </Button>
          )}
        </FileButton>
        {value && (
          <>
            <Text size="xs" c="dimmed">
              {paperKindWord(value.contentType)} · {readableSize(value.sizeBytes)}
            </Text>
            <Anchor component="button" type="button" size="xs" onClick={() => setViewing(true)}>
              Check it
            </Anchor>
            <Anchor
              component="button"
              type="button"
              size="xs"
              c="dimmed"
              onClick={() => onChange(null)}
              // Only unhooks it from this form. Nothing is destroyed — see the attach routes.
            >
              Take it off
            </Anchor>
          </>
        )}
      </Group>
      {refusal && (
        <Text size="xs" c="red">
          {refusal}
        </Text>
      )}
      {viewing && value && (
        <PaperViewer documentId={value.id} title="The file you attached" onClose={() => setViewing(false)} />
      )}
    </Stack>
  );
}

// ------------------------------------------------------------------ on a record that already exists

/**
 * The photograph on a bill or a delivery that is already written down.
 *
 * This is the one that matters most, because the photograph almost never arrives with the figure.
 * The bill is typed off the paper on the counter; the picture is found in a phone's camera roll
 * weeks later, on the day somebody asks whether the meter reading was right.
 *
 * `attachPath` is the record's own document endpoint — the caller names the record, this names
 * nothing about it, and the two never disagree about which bill is being changed.
 */
export function PaperOnRecord({
  documentId,
  attachPath,
  subject,
  invalidate,
  emptyNote = 'No photograph of this bill.',
  kind = 'invoice',
}: {
  documentId: string | null | undefined;
  attachPath: string;
  subject: string;
  /** Query keys to refresh once the record's document changes. */
  invalidate?: readonly (readonly unknown[])[];
  emptyNote?: string;
  kind?: 'invoice' | 'photo' | 'statement' | 'other';
}) {
  const [viewing, setViewing] = useState(false);
  const { attach, remove, busy } = usePaperAttachment(attachPath, subject, kind, invalidate);

  // Only asked when there IS one. The answer is what tells "gone" apart from "never taken", and
  // asking about a bill with no photograph would be a request to learn nothing.
  const paper = useQuery({
    queryKey: detailKey(DOCUMENTS_PATH, documentId ?? ''),
    queryFn: () => rawApi<StoredPaper>('GET', `${DOCUMENTS_PATH}/${documentId}`),
    enabled: Boolean(documentId),
    retry: false,
  });

  // A row that points at a document the office cannot find. Either the record of it is gone or the
  // file is: the same thing to the person reading, and a different thing from never having one.
  const lost = Boolean(documentId) && (paper.isError || paper.data?.available === false);

  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        The paper itself
      </Text>

      {!documentId ? (
        <Text size="xs" c="dimmed">
          {emptyNote} If you photographed it, attach it now — it is what answers the question when the paper
          has gone.
        </Text>
      ) : lost ? (
        <Alert color="yellow" variant="light" title="The photograph is no longer stored">
          One was attached to {subject} and the file has since gone. If you still have the paper, photograph
          it again and attach the new one.
        </Alert>
      ) : paper.isLoading ? (
        <Loader size="xs" />
      ) : (
        <Group gap="sm">
          <Anchor component="button" type="button" size="sm" onClick={() => setViewing(true)}>
            See the paper
          </Anchor>
          {paper.data && (
            <Text size="xs" c="dimmed">
              {paperKindWord(paper.data.contentType)} · {readableSize(paper.data.sizeBytes)} · attached{' '}
              {formatDate(paper.data.uploadedAt)}
            </Text>
          )}
        </Group>
      )}

      <Group gap="sm" mt={4}>
        <FileButton onChange={(file) => file && attach.mutate(file)} accept={ACCEPT}>
          {(props) => (
            <Button {...props} size="compact-xs" variant="default" loading={attach.isPending} disabled={busy}>
              {documentId ? 'Replace it' : 'Attach a photo or PDF'}
            </Button>
          )}
        </FileButton>
        {documentId && (
          <Button
            size="compact-xs"
            variant="subtle"
            color="gray"
            loading={remove.isPending}
            disabled={busy}
            onClick={() => remove.mutate()}
          >
            Take it off
          </Button>
        )}
      </Group>

      {viewing && documentId && (
        <PaperViewer documentId={documentId} title={subject} onClose={() => setViewing(false)} />
      )}
    </Stack>
  );
}
